/**
 * Transaction process graph for event-kit-rental-v1
 *
 * RTE's custom transaction process with:
 * - 24h acceptance window
 * - Dual-party pickup/return handoff
 * - Dispute resolution
 * - Sliding cancellation refund
 * - 14-day review window
 */

// ============================================================
// TRANSITIONS
// ============================================================

export const transitions = {
  // Pre-booking
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Payment
  EXPIRE_PAYMENT: 'transition/expire-payment',
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // Accept / Decline / Expire
  ACCEPT: 'transition/accept',
  OPERATOR_ACCEPT: 'transition/operator-accept',
  DECLINE: 'transition/decline',
  OPERATOR_DECLINE: 'transition/operator-decline',
  EXPIRE: 'transition/expire',

  // Cancellations
  CUSTOMER_CANCEL_PRE_ACCEPT: 'transition/customer-cancel-pre-accept',
  CUSTOMER_CANCEL_POST_ACCEPT: 'transition/customer-cancel-post-accept',
  PROVIDER_CANCEL: 'transition/provider-cancel',
  OPERATOR_CANCEL_ACCEPTED: 'transition/operator-cancel-accepted',
  OPERATOR_CANCEL_PICKUP_PENDING: 'transition/operator-cancel-pickup-pending',
  OPERATOR_CANCEL_PICKED_UP: 'transition/operator-cancel-picked-up',

  // Pickup handoff
  AUTO_PICKUP_PENDING: 'transition/auto-pickup-pending',
  PROVIDER_CONFIRM_PICKUP: 'transition/provider-confirm-pickup',
  RENTER_CONFIRM_PICKUP_AFTER_PROVIDER: 'transition/renter-confirm-pickup-after-provider',
  RENTER_CONFIRM_PICKUP: 'transition/renter-confirm-pickup',
  PROVIDER_CONFIRM_PICKUP_AFTER_RENTER: 'transition/provider-confirm-pickup-after-renter',
  OPERATOR_COMPLETE_PICKUP: 'transition/operator-complete-pickup',
  OPERATOR_COMPLETE_PICKUP_FROM_PROVIDER: 'transition/operator-complete-pickup-from-provider',
  OPERATOR_COMPLETE_PICKUP_FROM_RENTER: 'transition/operator-complete-pickup-from-renter',

  // Return handoff
  AUTO_RETURN_PENDING: 'transition/auto-return-pending',
  RENTER_SUBMIT_RETURN: 'transition/renter-submit-return',
  AUTO_RETURN_LATE: 'transition/auto-return-late',
  RENTER_SUBMIT_RETURN_LATE: 'transition/renter-submit-return-late',
  PROVIDER_ACCEPT_RETURN: 'transition/provider-accept-return',
  PROVIDER_OPEN_CLAIM: 'transition/provider-open-claim',
  OPERATOR_COMPLETE_RETURN: 'transition/operator-complete-return',
  OPERATOR_RESOLVE_LATE: 'transition/operator-resolve-late',

  // Dispute
  OPERATOR_RESOLVE_PARTIAL: 'transition/operator-resolve-partial',
  OPERATOR_RESOLVE_FULL: 'transition/operator-resolve-full',

  // Reviews
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
};

// ============================================================
// STATES
// ============================================================

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  CANCELLED_BY_CUSTOMER: 'cancelled-by-customer',
  CANCELLED_BY_PROVIDER: 'cancelled-by-provider',

  // Pickup handoff
  PICKUP_PENDING: 'pickup-pending',
  PICKUP_PROVIDER_CONFIRMED: 'pickup-provider-confirmed',
  PICKUP_RENTER_CONFIRMED: 'pickup-renter-confirmed',
  PICKED_UP: 'picked-up',

  // Return handoff
  RETURN_PENDING: 'return-pending',
  RETURN_RENTER_SUBMITTED: 'return-renter-submitted',
  RETURN_LATE: 'return-late',

  // Completion + dispute
  COMPLETED: 'completed',
  DISPUTED: 'disputed',
  RESOLVED_PARTIAL: 'resolved-partial',
  RESOLVED_FULL: 'resolved-full',

  // Reviews
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

// ============================================================
// GRAPH
// ============================================================

export const graph = {
  id: 'event-kit-rental-v1/release-2',
  initial: states.INITIAL,
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },
    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },
    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.CUSTOMER_CANCEL_PRE_ACCEPT]: states.CANCELLED_BY_CUSTOMER,
      },
    },
    [states.ACCEPTED]: {
      on: {
        [transitions.CUSTOMER_CANCEL_POST_ACCEPT]: states.CANCELLED_BY_CUSTOMER,
        [transitions.PROVIDER_CANCEL]: states.CANCELLED_BY_PROVIDER,
        [transitions.OPERATOR_CANCEL_ACCEPTED]: states.CANCELLED_BY_PROVIDER,
        [transitions.AUTO_PICKUP_PENDING]: states.PICKUP_PENDING,
      },
    },
    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.CANCELLED_BY_CUSTOMER]: {},
    [states.CANCELLED_BY_PROVIDER]: {},

    // Pickup handoff (either party can confirm first)
    [states.PICKUP_PENDING]: {
      on: {
        [transitions.PROVIDER_CONFIRM_PICKUP]: states.PICKUP_PROVIDER_CONFIRMED,
        [transitions.RENTER_CONFIRM_PICKUP]: states.PICKUP_RENTER_CONFIRMED,
        [transitions.OPERATOR_COMPLETE_PICKUP]: states.PICKED_UP,
        [transitions.OPERATOR_CANCEL_PICKUP_PENDING]: states.CANCELLED_BY_PROVIDER,
      },
    },
    [states.PICKUP_PROVIDER_CONFIRMED]: {
      on: {
        [transitions.RENTER_CONFIRM_PICKUP_AFTER_PROVIDER]: states.PICKED_UP,
        [transitions.OPERATOR_COMPLETE_PICKUP_FROM_PROVIDER]: states.PICKED_UP,
      },
    },
    [states.PICKUP_RENTER_CONFIRMED]: {
      on: {
        [transitions.PROVIDER_CONFIRM_PICKUP_AFTER_RENTER]: states.PICKED_UP,
        [transitions.OPERATOR_COMPLETE_PICKUP_FROM_RENTER]: states.PICKED_UP,
      },
    },
    [states.PICKED_UP]: {
      on: {
        [transitions.AUTO_RETURN_PENDING]: states.RETURN_PENDING,
        [transitions.OPERATOR_CANCEL_PICKED_UP]: states.CANCELLED_BY_PROVIDER,
      },
    },

    // Return handoff
    [states.RETURN_PENDING]: {
      on: {
        [transitions.RENTER_SUBMIT_RETURN]: states.RETURN_RENTER_SUBMITTED,
        [transitions.AUTO_RETURN_LATE]: states.RETURN_LATE,
      },
    },
    [states.RETURN_LATE]: {
      on: {
        [transitions.RENTER_SUBMIT_RETURN_LATE]: states.RETURN_RENTER_SUBMITTED,
        [transitions.OPERATOR_RESOLVE_LATE]: states.COMPLETED,
      },
    },
    [states.RETURN_RENTER_SUBMITTED]: {
      on: {
        [transitions.PROVIDER_ACCEPT_RETURN]: states.COMPLETED,
        [transitions.PROVIDER_OPEN_CLAIM]: states.DISPUTED,
        [transitions.OPERATOR_COMPLETE_RETURN]: states.COMPLETED,
      },
    },

    // Dispute resolution
    [states.DISPUTED]: {
      on: {
        [transitions.OPERATOR_RESOLVE_PARTIAL]: states.RESOLVED_PARTIAL,
        [transitions.OPERATOR_RESOLVE_FULL]: states.RESOLVED_FULL,
      },
    },
    [states.RESOLVED_PARTIAL]: {},
    [states.RESOLVED_FULL]: {},

    // Completion + Reviews
    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.CUSTOMER_CANCEL_PRE_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.PROVIDER_CANCEL,
    transitions.AUTO_PICKUP_PENDING,
    transitions.PROVIDER_CONFIRM_PICKUP,
    transitions.RENTER_CONFIRM_PICKUP,
    transitions.RENTER_CONFIRM_PICKUP_AFTER_PROVIDER,
    transitions.PROVIDER_CONFIRM_PICKUP_AFTER_RENTER,
    transitions.AUTO_RETURN_PENDING,
    transitions.RENTER_SUBMIT_RETURN,
    transitions.AUTO_RETURN_LATE,
    transitions.RENTER_SUBMIT_RETURN_LATE,
    transitions.PROVIDER_ACCEPT_RETURN,
    transitions.PROVIDER_OPEN_CLAIM,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_FULL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.OPERATOR_CANCEL_PICKUP_PENDING,
    transitions.OPERATOR_CANCEL_PICKED_UP,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_LATE,
  ].includes(transition);
};

export const isCompleted = transition => {
  return [
    transitions.PROVIDER_ACCEPT_RETURN,
    transitions.OPERATOR_COMPLETE_RETURN,
    transitions.OPERATOR_RESOLVE_LATE,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_FULL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ].includes(transition);
};

export const isRefunded = transition => {
  return [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.CUSTOMER_CANCEL_PRE_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.PROVIDER_CANCEL,
    transitions.OPERATOR_CANCEL_ACCEPTED,
    transitions.OPERATOR_CANCEL_PICKUP_PENDING,
    transitions.OPERATOR_CANCEL_PICKED_UP,
  ].includes(transition);
};

// Only flag states where the user actually needs to take action. Adding
// mid-lifecycle states here made the badge show for every active booking even
// when nothing had happened, so the user would click Inbox and see nothing
// new. Proper unread-message counts require a separate implementation.
export const statesNeedingProviderAttention = [
  states.PREAUTHORIZED,
  states.PICKUP_PENDING,
  states.RETURN_RENTER_SUBMITTED,
];

export const statesNeedingCustomerAttention = [
  states.PICKUP_PENDING,
  states.RETURN_PENDING,
  states.RETURN_LATE,
];
